import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { QueryState } from '@/components/QueryState';
import { shortDate, today } from '@/lib/format';
import { useCreateWorker, useWorkers } from '@/lib/queries';
import type { Trade, Uuid } from '@/lib/types';

const TRADES: Trade[] = ['건축', '구조', '기계', '전기', '통신', '토목', '조경', '기타'];

/**
 * 옛 주소 `/sites/:siteId/workers` — 출역 화면의 「투입 인력」 탭으로 보낸다(DOC-84 §7 ②).
 * 즐겨찾기와 위젯이 아직 이 주소를 부를 수 있어 없애지 않는다.
 */
export function WorkersPage() {
  const { siteId = '' } = useParams();
  return <Navigate to={`/sites/${siteId}/labor?tab=workers`} replace />;
}

/**
 * 투입 인력 탭 — 출역 화면의 둘째 탭이다({@link LaborPage}).
 *
 * <p>지금 출역은 <b>인원수만</b> 남는다(`pmis_labor_records.headcount`) — 누가 들어왔는지가
 * 어디에도 없다(DOC-81 #12). 이 탭이 그 자리를 만든다. 사람이 남아야 노무비·퇴직공제
 * 신고·안전보건 서류가 붙을 수 있다.
 *
 * <p><b>안전교육을 안 받은 사람은 현장에 들어오면 안 된다.</b> 그래서 목록에서 그것부터 보인다.
 */
export function WorkersTab({ siteId, canWrite, open, onClose }: { siteId: Uuid; canWrite: boolean; open: boolean; onClose: () => void }) {
  const workers = useWorkers(siteId);
  const create = useCreateWorker(siteId);

  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [phone, setPhone] = useState('');
  const [trade, setTrade] = useState<Trade>('건축');
  const [jobTitle, setJobTitle] = useState('');
  const [enteredOn, setEnteredOn] = useState(today);
  const [safetyEducated, setSafetyEducated] = useState(false);
  const [healthChecked, setHealthChecked] = useState(false);

  const rows = workers.data ?? [];
  const active = rows.filter((row) => row.leftOn === null);
  const left = rows.filter((row) => row.leftOn !== null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    await create.mutateAsync({
      name: name.trim(),
      birthDate: birthDate === '' ? null : birthDate,
      phone: phone.trim() === '' ? null : phone.trim(),
      trade,
      jobTitle: jobTitle.trim() === '' ? null : jobTitle.trim(),
      enteredOn,
      safetyEducated,
      healthChecked,
    });
    onClose();
    setName('');
    setBirthDate('');
    setPhone('');
    setJobTitle('');
    setSafetyEducated(false);
    setHealthChecked(false);
  }

  return (
    <>
      {open && canWrite && (
        <form onSubmit={onSubmit} className="card mb-3">
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>이름</span>
            <input type="text" className="input-field" value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <div className="mb-3 flex gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>생년월일</span>
              <input type="date" className="input-field" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
            </label>
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>투입일</span>
              <input type="date" className="input-field" value={enteredOn} onChange={(e) => setEnteredOn(e.target.value)} required />
            </label>
          </div>
          <div className="mb-3 flex gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>공종</span>
              <select className="input-field" value={trade} onChange={(e) => setTrade(e.target.value as Trade)}>
                {TRADES.map((it) => <option key={it} value={it}>{it}</option>)}
              </select>
            </label>
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>직종</span>
              <input type="text" className="input-field" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="철근공" />
            </label>
          </div>
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>연락처</span>
            <input type="tel" inputMode="tel" className="input-field" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </label>

          <div className="mb-4 flex flex-col gap-2">
            <label className="flex items-center gap-2 text-ui-sm">
              <input type="checkbox" checked={safetyEducated} onChange={(e) => setSafetyEducated(e.target.checked)} />
              안전교육 이수
            </label>
            <label className="flex items-center gap-2 text-ui-sm">
              <input type="checkbox" checked={healthChecked} onChange={(e) => setHealthChecked(e.target.checked)} />
              배치전 건강진단
            </label>
          </div>

          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1" disabled={create.isPending}>
              {create.isPending ? '보내는 중입니다…' : '등록'}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>취소</button>
          </div>
        </form>
      )}

      <QueryState
        pending={workers.isPending}
        error={workers.isError}
        empty={rows.length === 0}
        emptyText="등록된 인력이 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {[...active, ...left].map((row) => (
            <li key={row.id} className="card flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-ui-md">
                  {row.name}
                  {row.jobTitle !== null && (
                    <span className="ml-1 text-ui-2xs" style={{ color: 'var(--text-muted)' }}>{row.jobTitle}</span>
                  )}
                </div>
                <div className="truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                  {shortDate(row.enteredOn)} 투입
                  {row.leftOn !== null && ` · ${shortDate(row.leftOn)} 철수`}
                </div>
              </div>
              <div className="flex shrink-0 gap-1">
                {!row.safetyEducated && <span className="badge badge-red">안전교육</span>}
                {!row.healthChecked && <span className="badge badge-red">건강진단</span>}
                {row.leftOn !== null && <span className="badge badge-gray">철수</span>}
              </div>
            </li>
          ))}
        </ul>
      </QueryState>
    </>
  );
}
